import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import { useIntegrations } from "@/hooks/use-integrations";
import type { PersonifyEventLog } from "@/types/integrations";
import { Button, SinglePageTable } from "@/ui";
import { formatShortDate } from "@/utils";
import { InfoField, JsonDisplay } from "../../-components";
import { DetachIdentityModal } from "./-components";

const eventColumns: ColumnDef<PersonifyEventLog>[] = [
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: "When",
    cell: ({ row }) => {
      return formatShortDate(row.original.createdAt);
    },
  },
  {
    id: "eventType",
    accessorKey: "eventType",
    header: "Event",
    cell: ({ row }) => {
      return <span className="font-mono text-xs">{row.original.eventType}</span>;
    },
  },
  {
    id: "trigger",
    accessorKey: "trigger",
    header: "Trigger",
    cell: ({ row }) => {
      return row.original.trigger ?? "—";
    },
  },
  {
    id: "payload",
    accessorKey: "payload",
    header: "Payload",
    cell: ({ row }) => {
      return row.original.payload ? (
        <pre className="text-xs whitespace-pre-wrap">
          {JSON.stringify(row.original.payload, null, 2)}
        </pre>
      ) : (
        "—"
      );
    },
  },
];

const PersonifyIdentityDetailPage = () => {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [showDetachModal, setShowDetachModal] = useState(false);

  const { data: identity, error, isLoading } = useIntegrations().getPersonifyIdentity(id);
  const { isPending: isDetaching, mutate: detach } = useIntegrations().detachPersonifyIdentity();

  const infoFields = useMemo(() => {
    if (!identity) {
      return [];
    }

    const fields: { label: string; value: string | number }[] = [
      { label: "Identity ID", value: identity.id },
      { label: "External Member ID", value: identity.externalMemberId ?? "—" },
      { label: "Sponsor ID", value: identity.externalSponsorId ?? "—" },
      { label: "Eligibility", value: identity.eligibilityStatus ?? "—" },
      { label: "Email at Link", value: identity.emailAtLink ?? "—" },
      { label: "User", value: identity.userEmail ?? "(no linked user)" },
      { label: "Subject", value: identity.subject ?? "—" },
      {
        label: "Linked At",
        value: identity.linkedAt ? formatShortDate(identity.linkedAt) : "—",
      },
      {
        label: "Last Login",
        value: identity.lastLoginAt ? formatShortDate(identity.lastLoginAt) : "—",
      },
      {
        label: "Profiling Token",
        value: identity.profilingLinkToken ? "present" : "(cleared / used)",
      },
      {
        label: "Profiling Used At",
        value: identity.profilingLinkUsedAt ? formatShortDate(identity.profilingLinkUsedAt) : "—",
      },
    ];

    return fields;
  }, [identity]);

  if (isLoading) {
    return <div className="text-sm text-content-muted">Loading…</div>;
  }

  if (error || !identity) {
    return (
      <div className="rounded-xl bg-danger-surface p-4 text-sm text-danger-content">
        Failed to load identity.
        {error instanceof Error ? <p className="mt-1 text-xs">{error.message}</p> : null}
      </div>
    );
  }

  const handleDetach = () => {
    detach(identity.id, {
      onSuccess: () => {
        setShowDetachModal(false);
        void navigate({ to: "/integrations/personify/identities" });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          disabled={isDetaching}
          onClick={() => {
            return setShowDetachModal(true);
          }}
          variant="secondary"
        >
          Detach identity
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-xl bg-surface p-4">
        {infoFields.map((field) => {
          return <InfoField key={field.label} {...field} />;
        })}
      </div>

      <JsonDisplay data={identity.rawClaims} label="Raw Claims" />

      <div className="flex flex-col gap-2 rounded-xl bg-surface p-4">
        <strong>Recent events</strong>

        {identity.recentEvents.length === 0 ? (
          <p className="text-sm text-content-muted">No events recorded.</p>
        ) : (
          <SinglePageTable
            columns={eventColumns}
            data={identity.recentEvents}
            isLoadingData={false}
            rowsPerPage={50}
            setSorting={() => {}}
            sorting={[]}
          />
        )}
      </div>

      <DetachIdentityModal
        isDetaching={isDetaching}
        isOpen={showDetachModal}
        onClose={() => {
          return setShowDetachModal(false);
        }}
        onConfirm={handleDetach}
      />
    </div>
  );
};

export const Route = createFileRoute("/_private/integrations/personify/identities/$id/")({
  component: PersonifyIdentityDetailPage,
  params: {
    parse: (params: Record<string, string>) => {
      return { id: z.number().int().parse(Number(params.id)) };
    },
  },
});
